import {
  type SessionData,
  type RequestToken,
  type AbortVariant,
} from '../types';

// -- State machine --

export type AppState =
  | { type: 'landing' }
  | { type: 'loading' }
  | { type: 'appInstall'; appName: string }
  | { type: 'error'; variant: AbortVariant; canLeave: boolean }
  | { type: 'mobileFlowStart'; session: SessionData }
  | { type: 'appSelect'; session: SessionData }
  | {
      type: 'issuanceReview';
      session: SessionData;
      selectedToken: RequestToken;
    }
  | { type: 'chulak'; session: SessionData; selectedToken: RequestToken };

export type AppAction =
  | {
      action: 'INIT_SUCCESS';
      session: SessionData;
      autoSelectedToken: RequestToken | null;
      isMobile: boolean;
    }
  | { action: 'INIT_ERROR'; variant: AbortVariant; canLeave?: boolean }
  | { action: 'MOBILE_CONTINUE' }
  | { action: 'SELECT_TOKEN'; token: RequestToken }
  | { action: 'REVIEW_ACCEPTED' }
  | { action: 'BACK_TO_PICKER' };

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.action) {
    case 'INIT_SUCCESS': {
      const { session, autoSelectedToken, isMobile } = action;
      if (isMobile) return { type: 'mobileFlowStart', session };
      if (autoSelectedToken)
        return resolveTokenSelected(session, autoSelectedToken);
      return { type: 'appSelect', session };
    }
    case 'INIT_ERROR':
      return {
        type: 'error',
        variant: action.variant,
        // Errors where no client redirect is possible hide the "Leave" action.
        canLeave: action.canLeave ?? true,
      };
    case 'MOBILE_CONTINUE': {
      if (state.type !== 'mobileFlowStart') return state;
      const { session } = state;
      if (session.tokens.length === 1)
        return resolveTokenSelected(session, session.tokens[0]);
      return { type: 'appSelect', session };
    }
    case 'SELECT_TOKEN': {
      if (state.type !== 'appSelect') return state;
      return resolveTokenSelected(state.session, action.token);
    }
    case 'REVIEW_ACCEPTED': {
      if (state.type !== 'issuanceReview') return state;
      return {
        type: 'chulak',
        session: state.session,
        selectedToken: state.selectedToken,
      };
    }
    case 'BACK_TO_PICKER': {
      if (state.type !== 'chulak' && state.type !== 'issuanceReview')
        return state;
      return { type: 'appSelect', session: state.session };
    }
  }
}

function resolveTokenSelected(
  session: SessionData,
  token: RequestToken,
): AppState {
  const isIssuance = session.context.request.type === 'issuance';
  if (isIssuance && session.interactionConfig.requireExplicitConsent) {
    return { type: 'issuanceReview', session, selectedToken: token };
  }
  return { type: 'chulak', session, selectedToken: token };
}

// -- Init --

export function getInitialState(): AppState {
  const searchParams = new URLSearchParams(window.location.search);
  const appInstallParam = searchParams.get('app');
  if (appInstallParam) return { type: 'appInstall', appName: appInstallParam };
  if (searchParams.has('oauth_interaction_start_uri'))
    return { type: 'loading' };
  // Embedded mode: start in loading and wait for the parent's ronan:init.
  try {
    if (window.self !== window.top) return { type: 'loading' };
  } catch {
    return { type: 'loading' };
  }
  return { type: 'landing' };
}
